import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from '..';
import { DsArea, OrganType, WageAndTravelCategory, WageAndTravelStatus, WageAndTravelType } from '@nkr/enums/index';
import { IWatrRequest, IWatrReimbursement, IWatrRequestAuditLog, IListItem, IBaseResponse, IWatrRequestCounts, IWatrDuplicateCounts, IWatrRequestTravels, IWatrChaseExport, ICenterBase, IWatrPaymentError, IWatrChaseImport, ApprovalEmailContract, IClaimDeclineResponseItem, IPendingRegistrationDonor } from '../../models/index';
import { IDonorShieldDirectResponse } from '@nkr/store/index';

export interface IWatrRequestResponse {
    data: {
        success: boolean,
        results: IWatrRequest[]
    }
}

export interface IWatrRequestCountsResponse {
    data: {
        success: boolean,
        results: IWatrRequestCounts
    }
}

export interface IPendingRegistrationDonorsResponse {
    data: {
        success: boolean,
        results: IPendingRegistrationDonor[]
    }
}

export interface IWatrRequestLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donationTypes: IListItem[],
            statusTypes: IListItem[],
            requestTypes: IListItem[],
            auditLogTypes: IListItem[],
            logActionTypes: IListItem[],
            reimbursementTypes: IListItem[],
            centerList: ICenterBase[],
            additionalWeeksReasonList: IListItem[],
            claimDeclineResponseList: IClaimDeclineResponseItem[],
            categoryList: IListItem[],
            stateList: IListItem[],
            countryList: IListItem[],
            mileageRates?: Array<{ year: number; rate: number }>
        }
    }
}

export interface IWatrDuplicateCountResponse {
    data: {
        success: boolean,
        results: IWatrDuplicateCounts
    }
}

export interface IWatrAuditLogResponse {

    data: {
        success: boolean,
        results: IWatrRequestAuditLog
    }
}

export interface IWatrRequestUpdateResponse {
    data: {
        success: boolean,
        results: IWatrRequest,
        message: string
    }
}

export interface IWatrReimbursementResponse {
    data: {
        success: boolean,
        results: IWatrReimbursement[]
    }
}

export interface IWatAddReimbursementResponse {
    data: {
        success: boolean,
        results: IWatrReimbursement
    }
}

export interface IWatrLogsResponse {
    data: {
        success: boolean,
        results: IWatrRequestAuditLog[]
    }
}

export interface IReimbursementEntryResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            newReimbursement: IWatrReimbursement,
            newRequestStatus: number
        }
    }
}

export interface IWatrConfirmReimbursementItem {
    requestId: number,
    reimbursementType: number,
    amount: number,
    confirmationCode: string,
    date: string
}

export interface IConfirmReimbursementsResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            confirmed: number,
            failed: number,
            items: {
                requestId: number,
                reimbursementType: number,
                success: boolean,
                message: string
            }[]
        }
    }
}

export interface IReimbursementUpdateResponse {
    data: {
        success: boolean,
        message: string,
        results: IWatrReimbursement
    }
}

export interface IWatrYearEndResponse {
    data: {
        success: boolean,
        message: string,
        results: IWatrReimbursement[]
    }
}

export interface IWatrAuditReportResponse {
    data: {
        success: boolean,
        message: string,
        results: IWatrReimbursement[]
    }
}

export interface IWatrTransactionsResponse {
    data: {
        success: boolean,
        message: string,
        results: IWatrReimbursement[]
    }
}

export interface IWatrTravelVerifyResponse {
    data: {
        succes: boolean,
        result: IWatrRequestTravels
    }
}

export interface IWatrChaseExportListResponse {
    data: {
        success: boolean,
        results: IWatrChaseExport[]
    }
}

export interface IWatrChaseUploadResponse {
    data: {
        success: boolean,
        results: IWatrRequest[]
        matchFailures?: IWatrChaseImport[]
        message: string
    }
}

export interface IWatrDetailsData {
    watrBasicDetail: IWatrRequest,
    watrReimbursements: IWatrReimbursement[],
    watrTravelLogs: IWatrRequestAuditLog[]
}

export interface IWatrDetailsDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IWatrDetailsData
    }
}

export interface IWatrUpdateDonorRequest {
    requestId: number,
    firstName: string, middleInitial: string, lastName: string,
    email: string,
    mobile: string, birthDate: string, expirationDate: string,
    achAccountNumber: string, achRoutingNumber: string,
    intlBankingNumber: string, intlSwiftNumber: string,
    isUsBank?: boolean | null,
    bankCountry?: number | null,
    bankInformation: {
        nameOfBank: string,
        cityTownLocality: string,
        stateProvinceRegion: string,
        postalCode: string,
        additionalCode: string
    },
    addressLine1: string, // 14
    addressLine2: string, // 15
    city: string, // 16
    state: number, // 17
    postalCode: string, // 18
    country: number, // 19
    countryOfCitizenship: number, // 20
    isAdmin: Boolean,
    donationDate?: string
}

export const ActionCreators = {
    getRequestList: (centerId: number, area: DsArea, category: WageAndTravelCategory, startDate: string, endDate: string, isAdmin: boolean, organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestResponse> => {
        let url = ''

        if (centerId > 0) {
            url = `${__API__}/center/${centerId}/watr/requests?area=${area}&category=${category}&startDate=${startDate}&endDate=${endDate}&organType=${organType}`;
        } else if (isAdmin) {
            url = `${__API__}/admin/watr/requests?area=${area}&category=${category}&startDate=${startDate}&endDate=${endDate}&organType=${organType}`;
        }

        let options = {
            url: url
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    searchRequests: (centerId: number, firstName: string, lastName: string, email: string, birthDate: string, alias: string, startDate: string, endDate: string, area: DsArea, category: number, isAdmin: boolean, organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestResponse> => {
        let url = isAdmin ? `${__API__}/admin/watr/request/search` : `${__API__}/center/${centerId}/watr/request/search`;

        let options = {
            url: url,
            data: isAdmin
                ? {
                    firstName,
                    lastName,
                    email,
                    birthDate,
                    alias,
                    centerId,
                    area,
                    category,
                    startDate,
                    endDate,
                    organType
                }
                : {
                    firstName,
                    lastName,
                    email,
                    birthDate,
                    alias,
                    organType
                }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getRequestCounts: (centerId: number, area: DsArea, category: WageAndTravelCategory, startDate: string, endDate: string, isAdmin: boolean, organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestCountsResponse> => {
        let url = ''

        if (centerId > 0) {
            url = `${__API__}/center/${centerId}/watr/request-counts`;
            if (organType != null) {
                url += `?organType=${organType}`;
            }
        } else if (isAdmin) {
            url = `${__API__}/admin/watr/request-counts?area=${area}&category=${category}&startDate=${startDate}&endDate=${endDate}&organType=${organType}`;
        }

        let options = {
            url: url
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getPendingRegistrationDonors: (centerId: number, organType?: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IPendingRegistrationDonorsResponse> => {
        let url = `${__API__}/center/${centerId}/watr/pending-registration`;
        if (organType != null) {
            url += `?organType=${organType}`;
        }

        let options = {
            url: url
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getTravelDetails: (centerId: number, requestId: number, isAdmin: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrDetailsDataResponse> => {
        let url = `${__API__}/center/${centerId}/watr-request/${requestId}/travel-details`;

        if (isAdmin) {
            url = `${__API__}/admin/watr/request/${requestId}/travel-details`;
        }

        let options = {
            url
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrDetailsDataResponse) => {
            return Promise.resolve(response);
        });
    },

    getReimbursementsByRequestId: (requestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrReimbursementResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/reimbursements`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrReimbursementResponse) => {
            return Promise.resolve(response);
        });
    },

    getWatrRequestByDonorShieldId: (donorShieldId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${donorShieldId}/travel`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrRequestUpdateResponse) => {
            return Promise.resolve(response);
        });
    },

    getLogsByRequestId: (requestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrLogsResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/Logs`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrLogsResponse) => {
            return Promise.resolve(response);
        });
    },

    getRequestById: (centerId: number, requestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/watr/request/${requestId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrRequestResponse) => {
            return Promise.resolve(response);
        });
    },

    getYearEndReport: (year: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrYearEndResponse> => {
        let options = {
            url: `${__API__}/admin/watr/report/year-end?Year=` + year
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWatrYearEndResponse) => {
            return Promise.resolve(response);
        });
    },

    getAuditReport: (startDate: string, endDate: string, organType?: OrganType, query?: string) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrAuditReportResponse> => {
        let options = {
            url: `${__API__}/admin/watr/report/audit`,
            data: {
                startDate: startDate.toString(),
                endDate: endDate.toString(),
                exportCsv: false,
                organType: organType != null ? organType : null,
                query: query || null
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IWatrAuditReportResponse) => {
            return Promise.resolve(response);
        });
    },

    getTransactions: (startDate: string, endDate: string, getCsv: boolean, organType?: OrganType) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrTransactionsResponse> => {
        let url = `${__API__}/admin/watr/report/transactions`;

        let options = {
            url: url,
            data: {
                startDate: startDate.toString(),
                endDate: endDate.toString(),
                exportCsv: getCsv,
                organType: organType != null ? organType : null
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IWatrTransactionsResponse) => {
            return Promise.resolve(response);
        });
    },

    getLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestLookupDataResponse> => {
        let options = {
            url: `${__API__}/watr/lookup-data`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    updateDonorRequest: (request: IWatrUpdateDonorRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let { requestId, firstName, middleInitial, lastName, email, mobile, birthDate, expirationDate, donationDate,
            achAccountNumber, achRoutingNumber, intlBankingNumber, intlSwiftNumber,
            isUsBank, bankCountry,
            addressLine1, addressLine2, city, state, postalCode, country, countryOfCitizenship,
            bankInformation, isAdmin, } = request;

        let url = isAdmin
            ? '/admin/watr/request'
            : '/center/watr/request';

        let options = {
            url: `${__API__ + url}`,
            data: {
                Id: requestId,
                firstName,
                middleInitial,
                lastName,
                email,
                mobile,
                birthDate,
                expirationDate,
                donationDate,
                achAccountNumber,
                achRoutingNumber,
                intlBankingNumber,
                intlSwiftNumber,
                isUsBank,
                bankCountry,
                addressLine1,
                addressLine2,
                city,
                state,
                postalCode,
                country,
                countryOfCitizenship,
                bankInformation
            }
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IWatrRequestUpdateResponse) => {
            return Promise.resolve(response);
        });
    },

    createDonorRequest: (donorId: number, donorShieldId: number, firstName: string, middleInitial: string, lastName: string, email: string, mobile: string, birthDate: string,
        donationDate: string, addressLine1: string, addressLine2: string, city: string, state: number, postalCode: string, country: number, countryOfCitizenship: number, centerId: number, isAdmin: Boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {

            let url = isAdmin
                ? '/admin/watr/request'
                : '/center/watr/request';

            let options = {
                url: `${__API__ + url}`,
                data: {
                    donorShieldId,
                    donorId,
                    firstName,
                    middleInitial,
                    lastName,
                    email,
                    mobilePhone: mobile,
                    birthDate,
                    donationDate,
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    postalCode,
                    country,
                    countryOfCitizenship,
                    centerId
                }
            };

            return dispatch(AuthActionCreators.apiPost(options)).then((response: IWatrRequestUpdateResponse) => {
                return Promise.resolve(response);
            });
        },

    getDonorShieldDirectRecord: (donorShieldId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectResponse> => {
        var options = {
            url: `${__API__}/center/watr/request/${centerId}/get-dsd-watr-request/${donorShieldId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getDonorRecord: (donorId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectResponse> => {
        var options = {
            url: `${__API__}/center/watr/request/${centerId}/get-donor-watr-request/${donorId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getWatrRequestRecord: (watrId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        var options = {
            url: `${__API__}/center/${centerId}/watr/request/${watrId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    createAuditLog: (requestId: number, type: number, comments: string, file: File) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrAuditLogResponse> => {
        let formData = new FormData();

        formData.append('requestId', requestId.toString());
        formData.append('logtype', type.toString());
        formData.append('comments', comments);
        if (file && file.size > 0) {
            formData.append('files', file);
        }

        let options = {
            url: `${__API__}/center/watr/request/${requestId}/Log`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IWatrAuditLogResponse) => {
            return Promise.resolve(response);
        });
    },

    updateAuditLog: (requestId: number, logId: number, type: number, comments: string, file: File) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrAuditLogResponse> => {
        let formData = new FormData();

        formData.append('requestId', requestId.toString());
        formData.append('logtype', type.toString());
        formData.append('comments', comments);
        if (file && file.size > 0) {
            formData.append('files', file);
        }

        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/Log/${logId}`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPutFormData(options)).then((response: IWatrAuditLogResponse) => {
            return Promise.resolve(response);
        });
    },

    deleteAuditLog: (requestId: number, logId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrAuditLogResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/log/${logId}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IWatrAuditLogResponse) => {
            return Promise.resolve(response);
        });
    },

    createReimbursement: (requestId: number, type: number, amount: number, confirmationCode: string, date: string, closeAdvancedReimbursementCard?: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IReimbursementEntryResponse> => {
        let data: Record<string, unknown> = {
            amount: amount,
            confirmationCode: confirmationCode,
            reimbursementType: type,
            date: date
        };
        if (closeAdvancedReimbursementCard !== undefined) {
            data.closeAdvancedReimbursementCard = closeAdvancedReimbursementCard;
        }
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/Reimbursement`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    confirmReimbursements: (items: IWatrConfirmReimbursementItem[]) => async (dispatch: Dispatch<IApplicationState>): Promise<IConfirmReimbursementsResponse> => {
        let options = {
            url: `${__API__}/admin/watr/requests/reimbursements/confirm`,
            data: items
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    updateReimbursement: (requestId: number, reimbursementId: number, type: number, amount: number, confirmationCode: string, date: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IReimbursementUpdateResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/reimbursement/${reimbursementId}`,
            data: {
                id: reimbursementId,
                amount: amount,
                confirmationCode: confirmationCode,
                reimbursementType: type,
                date: date
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    verifyAmount: (requestId: number, status: WageAndTravelStatus, sendEmail: boolean, amount: number, emailProperties: ApprovalEmailContract) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/verify-amount`,
            data: {
                status,
                sendEmail,
                amount,
                emailProperties
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    verifyStub: (requestId: number, startDate: string, endDate: string, incomeGross: number, paystubs: any[], approvalEmail: ApprovalEmailContract, ssnDecrypted?: string, incomeIrsYear?: number, incomeIrsYearGross?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/verify-stub`,
            data: {
                startDate,
                endDate,
                incomeGross,
                paystubs: paystubs || [],
                ssnDecrypted,
                irsYear: incomeIrsYear,
                irsYearGross: incomeIrsYearGross,
                emailProperties: approvalEmail
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    verifyTax: (requestId: number, ssnEncrypted: string, irsYear: number, irsYearGross: number, approvalEmail: ApprovalEmailContract) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/verify-tax`,
            data: {
                ssnEncrypted,
                irsYear,
                irsYearGross,
                emailProperties: approvalEmail
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    verifyTravel: (travelRequestId: number, roundTripMiles: number, hotelLodgingExpense: number, flightAndTrainExpense: number, carServiceExpense: number, dependentCareExpense: number,
        mealExpense: number, isMilesOverWritten: boolean, overrideMiles: number, isMealsOverWritten: boolean, overrideMeals: number,
        isCarServiceExpenseOverridden: boolean, overrideCarServiceExpense: number,
        isDependentCareExpenseOverridden: boolean, overrideDependentCareExpense: number,
        isHotelLodgingExpenseOverridden: boolean, overrideHotelLodgingExpense: number, approvalEmail: ApprovalEmailContract) => async (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
            let options = {
                url: `${__API__}/admin/watr/request/${travelRequestId}/verify-travel`,
                data: {
                    roundTripMiles, hotelLodgingExpense, flightAndTrainExpense, carServiceExpense, dependentCareExpense,
                    mealExpense, isMilesOverWritten, overrideMiles, isMealsOverWritten, overrideMeals,
                    isCarServiceExpenseOverridden, overrideCarServiceExpense,
                    isDependentCareExpenseOverridden, overrideDependentCareExpense,
                    isHotelLodgingExpenseOverridden, overrideHotelLodgingExpense,
                    emailProperties: approvalEmail
                }
            };

            return await dispatch(AuthActionCreators.apiPost(options));
        },

    requestAdditionalDays: (requestId: number, additionalDays: number, additionalDaysSignOff: string, additionalDaysDrSignOff) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/request-additional-days`,
            data: {
                additionalDaysRequested: additionalDays,
                additionalDaysSignOff,
                additionalDaysDrSignOff
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    updatePaymentError: (paymentError: IWatrPaymentError) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${paymentError.requestId}/payment-error`,
            data: {
                ...paymentError
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    confirmDonation: (requestId: number, signOffInitials: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/confirm-donation`,
            data: {
                signOffInitials
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    updateDonationDate: (requestId: number, date: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/update-donation-date`,
            data: {
                donationDate: date,
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    updateWeekOtherAmount: (requestId: number, weekRequested: number, otherAmount: number, confirmedByInitials?: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/week-other-amount`,
            data: {
                weekRequested,
                otherAmount,
                ...(confirmedByInitials != null && confirmedByInitials !== ''
                    ? { confirmedByInitials }
                    : {})
            }
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    declineRequest: (requestId: number, emailSubject: string, firstParagraph: string, emailContent: string, lastParagraph: string, isTravel: boolean, isFixable: boolean, bccCenter: boolean, wageRepairRequested?: boolean, bankingRoutingRequiresReview?: boolean, bankingAccountRequiresReview?: boolean, declineReasonSummary?: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/decline?isTravel=${isTravel}`,
            data: {
                emailSubject,
                firstParagraph,
                emailContent,
                lastParagraph,
                isFixable,
                bccCenter,
                isTravel,
                wageRepairRequested: wageRepairRequested ?? false,
                bankingRoutingRequiresReview: bankingRoutingRequiresReview ?? false,
                bankingAccountRequiresReview: bankingAccountRequiresReview ?? false,
                declineReasonSummary: declineReasonSummary || null
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    cancelRequest: (requestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/cancel`,
            data: {}
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    updateAudit: (requestId: number, type: WageAndTravelType, status: WageAndTravelStatus, reimbursementAmount: number, donationDate: string, incomePercentCovered: number, sixtyDayOverride: boolean, ninetyDayLWROverride: boolean, expenseLimitOverride: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrRequestUpdateResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/audit`,
            data: {
                id: requestId,
                type: type,
                status: status,
                wageReimbursementAmount: reimbursementAmount,
                donationDate: donationDate,
                incomePercentCovered: incomePercentCovered,
                sixtyDayOverride: sixtyDayOverride,
                ninetyDayLWROverride: ninetyDayLWROverride,
                expenseLimitOverride: expenseLimitOverride
            }
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IWatrRequestUpdateResponse) => {
            return Promise.resolve(response);
        });
    },
    resendInvitation: (requestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/watr/request/${requestId}/resend-invite`
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    approveAdvancedReimbursement: (requestId: number, reason: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/advanced/approve`,
            data: { reason }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => Promise.resolve(response));
    },
    denyAdvancedReimbursement: (requestId: number, reason: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/advanced/deny`,
            data: { reason }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => Promise.resolve(response));
    },
    holdAdvancedReimbursement: (requestId: number, reason: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/advanced/hold`,
            data: { reason }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => Promise.resolve(response));
    },
    releaseAdvancedReimbursement: (requestId: number, reason: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/advanced/release`,
            data: { reason }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => Promise.resolve(response));
    },
    activateAdvancedReimbursement: (requestId: number, reason: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/request/${requestId}/advanced/activate`,
            data: { reason }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => Promise.resolve(response));
    },
    getDuplicateCounts: (watrId: number, email: string, mobile: string, donationOrganType: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrDuplicateCountResponse> => {
        let url = `${__API__}/center/watr/request/duplicate-counts?watrId=${watrId}&email=${encodeURIComponent(email)}&mobile=${encodeURIComponent(mobile)}&donationOrganType=${donationOrganType}`;
        let options = {
            url: url
        };
        return dispatch(AuthActionCreators.apiGet(options));
    },
    getChaseExport: (organType: OrganType) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrChaseExportListResponse> => {
        var options = {
            url: `${__API__}/admin/watr/chase-export`,
            data: { organType }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IWatrChaseExportListResponse) => {
            return Promise.resolve(response);
        });
    },
    getAchPayments: (organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/admin/watr/ach-payment?organType=${organType}`,
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    validateAchRouting: () => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/admin/watr/validate-ach-routing`,
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    uploadChaseTransactions: (file: File) => (dispatch: Dispatch<IApplicationState>): Promise<IWatrChaseUploadResponse> => {
        let formData = new FormData();

        formData.append('files', file);

        let options = {
            url: `${__API__}/admin/watr/upload-chase-transactions`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    uploadDevMockDocument: (args: {
        requestId: number;
        editKey: string;
        docType: number;
        lookupId: number;
        file: File;
    }) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        const formData = new FormData();
        formData.append('requestId', String(args.requestId));
        formData.append('editKey', args.editKey);
        formData.append('docType', String(args.docType));
        formData.append('lookupId', String(args.lookupId));
        formData.append('file', args.file);

        const options = {
            url: `${__API__}/admin/watr/dev-mock-document`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    getTripsByTravelRequestId: (travelRequestId: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/admin/watr/travel-request/${travelRequestId}/trips`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: any) => {
            return Promise.resolve(response);
        });
    },
    getLineItemsByTripId: (tripId: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/admin/watr/trip/${tripId}/line-items`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: any) => {
            return Promise.resolve(response);
        });
    },
    approveTrip: (tripId: number, requestId: number, requiresEmail: boolean, lineItemUpdates?: Array<{
        lineItemId: number,
        reviewRequired?: boolean,
        description?: string,
        amount?: number,
        expenseType?: number,
        expenseDate?: string,
        notes?: string,
        receiptFileId?: number
    }>) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/admin/watr/trip/${tripId}/approve`,
            data: {
                requestId: requestId,
                requiresEmail: requiresEmail,
                lineItemUpdates: lineItemUpdates || []
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: any) => {
            return Promise.resolve(response);
        });
    },
    declineTrip: (tripId: number, requestId: number, requiresEmail: boolean, lineItemUpdates?: Array<{
        lineItemId: number,
        reviewRequired?: boolean,
        description?: string,
        amount?: number,
        expenseType?: number,
        expenseDate?: string,
        notes?: string,
        receiptFileId?: number
    }>) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        const options = {
            url: `${__API__}/admin/watr/trip/${tripId}/decline`,
            data: {
                requestId: requestId,
                requiresEmail: requiresEmail,
                lineItemUpdates: lineItemUpdates || []
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: any) => {
            return Promise.resolve(response);
        });
    },
    declineTravelRequest: (travelRequestId: number, emailSubject: string, firstParagraph: string, emailContent: string, lastParagraph: string, isFixable: boolean, bccCenter: boolean, declineReasonSummary?: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/travel-request/${travelRequestId}/decline`,
            data: {
                emailSubject,
                firstParagraph,
                emailContent,
                lastParagraph,
                isFixable,
                bccCenter,
                isTravel: true,
                declineReasonSummary: declineReasonSummary || null
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    setPaystubReviewRequired: (paystubId: number, reviewRequired: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/watr/paystubs/${paystubId}/review-required`,
            data: {
                reviewRequired
            }
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },
};